/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { DateRangeType } from '../components/period-dropdown/DateRangePicker';
import { InitiativeBenchmarking, InitiativeCompany, SponsoredInitiative } from '../types/initiative';
import { DataPeriods } from '../types/universalTracker';
import { apiClient } from './api';

export interface PortfolioCompaniesFilters {
  dateRange?: DateRangeType;
  period?: DataPeriods | undefined;
  metrics?: string[];
}

export interface BenchmarkingSurveysFilters {
  dateRange?: DateRangeType;
  surveyPacks?: string[];
}

export const getPortfolioCompanies = async (portfolioId: string, recursive = true, filters?: PortfolioCompaniesFilters) => {
  return apiClient
    .get(`portfolios/${portfolioId}/companies${recursive ? '/recursive' : ''}`, { params: { ...filters } })
    .then((response) => response.data.data as InitiativeCompany[]);
}

export const createCompanyOnboarding = async (portfolioId: string, data: {}) => {
  return apiClient
    .post(`/portfolios/${portfolioId}/onboard`, data)
    .then((response) => response.data.data);
}

export const createCompanyOnboardingImport = async (portfolioId: string, data: {}) => {
  return apiClient
    .post(`/portfolios/${portfolioId}/onboard/import`, data)
    .then((response) => response.data.data);
}

export const getSponsorshipCodes = async (portfolioId: string): Promise<string[]> => {
  return apiClient
    .get(`/portfolios/${portfolioId}/sponsored-companies/referrer-codes`)
    .then((response) => response.data.data);
}

export const getSponsoredInitiatives = async (portfolioId: string, code: string): Promise<SponsoredInitiative[]> => {
  return apiClient
    .get(`/portfolios/${portfolioId}/sponsored-companies/referrer-codes/${code}`)
    .then((response) => response.data.data);
}

export const refreshSubscriptions = async (portfolioId: string, initiativeId: string): Promise<void> => {
  return apiClient
    .get(`/portfolios/${portfolioId}/sponsored-companies/refresh/${initiativeId}`)
    .then((response) => response.data.data);
}

export const addSponsorship = async (portfolioId: string, code: string, initiativeId: string): Promise<void> => {
  return apiClient
    .post(`/portfolios/${portfolioId}/sponsored-companies/referrer-codes/${code}/sponsor/${initiativeId}`)
    .then((response) => response.data.data);
}

export const cancelSponsorship = async (portfolioId: string, code: string, initiativeId: string): Promise<void> => {
  return apiClient
    .delete(`/portfolios/${portfolioId}/sponsored-companies/referrer-codes/${code}/sponsor/${initiativeId}`)
    .then((response) => response.data.data);
}

export const getPortfoliosByInitiativeId = (initiativeId: string) => apiClient
  .get(`initiatives/${initiativeId}/portfolios`)
  .then((response) => response.data.data);

export const createPortfolio = (initiativeId: string, name: string) => apiClient
  .post(`initiatives/${initiativeId}/portfolios`, { name })
  .then((response) => response.data.data);

export const getPortfolioById = (portfolioId: string) => apiClient
  .get(`portfolios/${portfolioId}`)
  .then((response) => response.data.data);

export const deletePortfolioById = (portfolioId: string) => apiClient
  .delete(`portfolios/${portfolioId}`)
  .then((response) => response.data.data);

export const addHoldingToPortfolio = (portfolioId: string, holdingId: string, weight: number) => apiClient
  .post(`portfolios/${portfolioId}/holdings`, { holdingId: holdingId, weight: weight })
  .then((response) => response.data.data);

export const removeHoldingFromPortfolio = (portfolioId: string, holdingId: string) => apiClient
  .delete(`portfolios/${portfolioId}/holdings/${holdingId}`)
  .then((response) => response.data.data);

export const updateHoldingWeightFromPortfolio = (portfolioId: string, holdingId: string, weight: number) => apiClient
  .patch(`portfolios/${portfolioId}/holdings/${holdingId}`, { weight })
  .then((response) => response.data.data);

export const getBenchmarkingSurveys = (portfolioId: string, filters?: BenchmarkingSurveysFilters) => {
  return apiClient
    .get(`portfolios/${portfolioId}/companies/benchmarking/latest-surveys`, { params: { ...filters } })
    .then((response) => response.data.data as InitiativeBenchmarking[]);
};
